import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import api from '../utils/api';
import { Send, Loader2, AlertCircle, Monitor, Code, Wifi, Key, HelpCircle, Megaphone, X, UserPlus, Paperclip, File, Image, FileText, Trash2, Upload } from 'lucide-react';

const categoryIcons = {
  monitor: Monitor,
  code: Code,
  wifi: Wifi,
  key: Key,
  megaphone: Megaphone,
  'help-circle': HelpCircle
};

const NewTicketModal = ({ isOpen, onClose }) => {
  const { user, isAgent, isSuperAdmin, accessibleOrganizations } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [organizationId, setOrganizationId] = useState(tenant?.id || '');
  const [categoryId, setCategoryId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Champs "pour le compte de"
  const [createForOther, setCreateForOther] = useState(false);
  const [requesterEmail, setRequesterEmail] = useState('');
  const [requesterName, setRequesterName] = useState('');

  // Date de création personnalisée
  const [createdAt, setCreatedAt] = useState('');

  // Pièces jointes
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Custom fields
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});

  const canCreateForOthers = isAgent || ['admin', 'superadmin'].includes(user?.role);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 5;

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const addFiles = (files) => {
    const fileList = Array.from(files);
    const currentCount = attachments.length;
    const remaining = MAX_FILES - currentCount;

    if (remaining <= 0) {
      setError(`Maximum ${MAX_FILES} fichiers autorisés`);
      return;
    }

    const filesToAdd = fileList.slice(0, remaining);
    const rejected = [];

    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name} dépasse 10 Mo`);
      }
    }

    if (rejected.length > 0) {
      setError(rejected.join(', '));
      return;
    }

    setAttachments(prev => [...prev, ...filesToAdd]);
    setError('');
  };

  const removeFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setOrganizationId(tenant?.id || '');
    }
  }, [isOpen, tenant?.id]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const orgId = organizationId || tenant?.id;
        if (orgId) {
          const res = await api.get('/tickets/categories/list', { params: { organizationId: orgId } });
          setCategories(res.data);
        }
      } catch (err) {
        console.error('Erreur fetch catégories:', err);
      }
    };
    if (isOpen) {
      fetchCategories();
    }
  }, [organizationId, tenant?.id, isOpen]);

  // Fetch custom fields when category changes
  useEffect(() => {
    const fetchCustomFields = async () => {
      if (!categoryId) { setCustomFields([]); setCustomFieldValues({}); return; }
      try {
        const res = await api.get(`/features/custom-fields/${categoryId}`);
        setCustomFields(res.data);
        // Initialize values
        const vals = {};
        res.data.forEach(f => { vals[f.id] = ''; });
        setCustomFieldValues(vals);
      } catch (err) {
        console.error('Erreur custom fields:', err);
        setCustomFields([]);
      }
    };
    fetchCustomFields();
  }, [categoryId]);

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setCategoryId('');
    setPriority('medium');
    setError('');
    setCreateForOther(false);
    setRequesterEmail('');
    setRequesterName('');
    setCreatedAt('');
    setCustomFields([]);
    setCustomFieldValues({});
    setAttachments([]);
    setDragActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation email si création pour autre
    if (createForOther && requesterEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(requesterEmail)) {
        setError('Adresse email invalide');
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        subject,
        message,
        organizationId: organizationId || undefined,
        categoryId: categoryId || undefined,
        priority,
        createdAt: createdAt || undefined
      };

      // Ajouter les infos du demandeur si création pour autre
      if (createForOther && requesterEmail) {
        payload.requesterEmail = requesterEmail;
        payload.requesterName = requesterName || undefined;
      }

      const res = await api.post('/tickets', payload);

      // Save custom field values if any
      if (customFields.length > 0 && res.data.id) {
        const fieldValues = Object.entries(customFieldValues)
          .filter(([, v]) => v !== '' && v !== undefined)
          .map(([fieldId, value]) => ({ fieldId: parseInt(fieldId), value }));
        if (fieldValues.length > 0) {
          try {
            await api.post(`/features/custom-fields/values/${res.data.id}`, { fieldValues });
          } catch (cfErr) {
            console.error('Erreur sauvegarde champs custom:', cfErr);
          }
        }
      }

      // Upload des pièces jointes
      if (attachments.length > 0 && res.data.id) {
        const formData = new FormData();
        formData.append('ticketId', res.data.id);
        attachments.forEach(file => formData.append('files', file));
        try {
          await api.post('/attachments/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadErr) {
          console.error('Erreur upload pièces jointes:', uploadErr);
        }
      }

      handleClose();
      navigate(`/tickets/${res.data.ticket_number}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Basse', description: 'Résolution sous 48h', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
    { value: 'medium', label: 'Moyenne', description: 'Résolution sous 24h', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    { value: 'high', label: 'Haute', description: 'Résolution sous 8h', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    { value: 'urgent', label: 'Urgente', description: 'Résolution sous 4h', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nouveau ticket</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Décrivez votre problème ou demande</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
            </div>
          )}

          <form id="new-ticket-form" onSubmit={handleSubmit} className="space-y-5">
            {isSuperAdmin && accessibleOrganizations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organisation</label>
                <select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} className="input">
                  {accessibleOrganizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                </select>
              </div>
            )}

            {/* Création pour le compte de (agents/admin uniquement) */}
            {canCreateForOthers && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForOther}
                    onChange={(e) => setCreateForOther(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    Créer pour le compte d'un utilisateur
                  </span>
                </label>

                {createForOther && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email du demandeur *
                      </label>
                      <input
                        type="email"
                        value={requesterEmail}
                        onChange={(e) => setRequesterEmail(e.target.value)}
                        className="input"
                        placeholder="utilisateur@exemple.com"
                        required={createForOther}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Si l'utilisateur n'existe pas, il sera créé automatiquement
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nom du demandeur
                      </label>
                      <input
                        type="text"
                        value={requesterName}
                        onChange={(e) => setRequesterName(e.target.value)}
                        className="input"
                        placeholder="Prénom Nom (optionnel)"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Catégorie */}
            {categories.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Catégorie</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {categories.map(cat => {
                    const IconComponent = categoryIcons[cat.icon] || HelpCircle;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategoryId(cat.id)}
                        className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          categoryId === cat.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <IconComponent className="w-6 h-6" style={{ color: cat.color }} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{cat.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Fields (dynamiques selon catégorie) */}
            {customFields.length > 0 && (
              <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Informations complémentaires</p>
                {customFields.map(field => {
                  const fieldOptions = field.field_options ? (typeof field.field_options === 'string' ? JSON.parse(field.field_options) : field.field_options) : [];
                  return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {field.field_label} {field.is_required ? '*' : ''}
                      </label>
                      {field.field_type === 'text' && (
                        <input
                          type="text"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                          className="input"
                          required={!!field.is_required}
                        />
                      )}
                      {field.field_type === 'textarea' && (
                        <textarea
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                          className="input min-h-[60px]"
                          required={!!field.is_required}
                        />
                      )}
                      {field.field_type === 'number' && (
                        <input
                          type="number"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                          className="input"
                          required={!!field.is_required}
                        />
                      )}
                      {field.field_type === 'date' && (
                        <input
                          type="date"
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                          className="input"
                          required={!!field.is_required}
                        />
                      )}
                      {field.field_type === 'select' && (
                        <select
                          value={customFieldValues[field.id] || ''}
                          onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: e.target.value })}
                          className="input"
                          required={!!field.is_required}
                        >
                          <option value="">Sélectionner...</option>
                          {(Array.isArray(fieldOptions) ? fieldOptions : []).map((opt, i) => (
                            <option key={i} value={typeof opt === 'string' ? opt : opt.value}>{typeof opt === 'string' ? opt : opt.label}</option>
                          ))}
                        </select>
                      )}
                      {field.field_type === 'checkbox' && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customFieldValues[field.id] === 'true'}
                            onChange={(e) => setCustomFieldValues({ ...customFieldValues, [field.id]: String(e.target.checked) })}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{field.field_label}</span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Priorité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priorité</label>
              <div className="grid grid-cols-2 gap-2">
                {priorityOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPriority(opt.value)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      priority === opt.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${opt.color}`}>
                      {opt.label}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Date de création personnalisée (agents uniquement) */}
            {canCreateForOthers && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date de création <span className="text-gray-400 font-normal">(optionnel)</span>
                </label>
                <input
                  type="datetime-local"
                  value={createdAt}
                  onChange={(e) => setCreatedAt(e.target.value)}
                  className="input"
                />
                <p className="text-xs text-gray-400 mt-1">Laisser vide pour utiliser la date actuelle</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sujet</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="input"
                placeholder="Résumé de votre demande"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input min-h-32 resize-none"
                placeholder="Décrivez votre problème en détail..."
                required
              />
            </div>

            {/* Pièces jointes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pièces jointes <span className="text-gray-400 font-normal">({attachments.length}/{MAX_FILES})</span>
              </label>
              <div
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('ticket-file-input').click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                } ${attachments.length >= MAX_FILES ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  id="ticket-file-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  disabled={attachments.length >= MAX_FILES}
                />
                <Upload className="w-6 h-6 mx-auto text-gray-400 dark:text-gray-500 mb-1" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Glissez vos fichiers ici ou <span className="text-blue-600 dark:text-blue-400 font-medium">parcourir</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Max {MAX_FILES} fichiers, 10 Mo chacun
                </p>
              </div>

              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((file, index) => {
                    const IconComp = getFileIcon(file);
                    return (
                      <li key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
                        <IconComp className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <span className="flex-1 truncate text-gray-700 dark:text-gray-300">{file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button type="button" onClick={handleClose} className="btn-secondary flex-1">
            Annuler
          </button>
          <button
            type="submit"
            form="new-ticket-form"
            disabled={loading}
            className="btn-primary flex-1"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" />Créer le ticket</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTicketModal;
